import { useEffect } from "react";
import { X } from "lucide-react";

export const LEGAL_TITLES = {
  mentions: "Mentions légales",
  confidentialite: "Politique de confidentialité",
  cookies: "Gestion des cookies",
};

function Block({ title, children }) {
  return (
    <div className="mt-10">
      <h3 className="font-display text-2xl italic">{title}</h3>
      <div className="hairline mt-3 w-10" />
      <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/80">{children}</div>
    </div>
  );
}

function Mentions() {
  return (
    <>
      <Block title="Éditeur et exploitant du site">
        <p>
          Le site L&rsquo;Eskale Guyane est édité par&nbsp;:
          <br />
          <strong>RYMAL</strong> — Société par actions simplifiée à associé unique (SASU)
          <br />
          Capital social&nbsp;: 1&nbsp;000&nbsp;€
          <br />
          Siège social&nbsp;: 20 chemin Germain, 97354 Rémire-Montjoly, Guyane française
          <br />
          RCS Cayenne&nbsp;: 980&nbsp;629&nbsp;794
          <br />
          Date d&rsquo;immatriculation&nbsp;: 18 octobre 2023
        </p>
        <p>
          Activité principale&nbsp;: création, acquisition et exploitation par bail, location ou autrement de
          résidences hôtelières ou hôtels.
        </p>
        <p>
          Directeur de la publication&nbsp;: Jean-Louis FORMAL, en qualité de Président de RYMAL.
        </p>
      </Block>
      <Block title="Contact">
        <p>
          L&rsquo;Eskale Guyane
          <br />
          20 Chemin Germain, 97354 Rémire-Montjoly, Guyane française
          <br />
          Téléphone / WhatsApp&nbsp;:{" "}
          <a href="tel:+594694449985" className="text-gold hover:underline">
            +594 694 44 99 85
          </a>
          <br />
          Site&nbsp;:{" "}
          <a href="https://leskaleguyane.net" className="text-gold hover:underline">
            leskaleguyane.net
          </a>
        </p>
      </Block>
      <Block title="Conception et réalisation">
        <p>
          Conception, direction digitale, UX/UI et réalisation du site&nbsp;: <strong>NS5 Agency</strong>.
        </p>
        <p>
          NS5 Agency intervient en qualité de prestataire chargé de la conception et de la réalisation du site.
        </p>
      </Block>
      <Block title="Hébergement">
        <p>
          Le site est techniquement hébergé par <strong>Emergent</strong> (
          <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
            emergent.sh
          </a>
          ).
        </p>
        <p>Le nom de domaine et sa zone DNS sont gérés via Wix.</p>
      </Block>
    </>
  );
}

function Confidentialite() {
  return (
    <>
      <Block title="Nature du site">
        <p>
          Le site leskaleguyane.net est un site vitrine de présentation. Il ne propose ni création de compte, ni
          paiement en ligne, et ne stocke aucune donnée personnelle sur ses propres serveurs.
        </p>
      </Block>
      <Block title="Demandes de réservation">
        <p>
          Les demandes de réservation et d&rsquo;information s&rsquo;effectuent via WhatsApp ou par téléphone. Les
          informations que vous nous communiquez à cette occasion (identité, dates de séjour, coordonnées) sont
          utilisées uniquement pour traiter votre demande et ne sont ni revendues, ni transmises à des tiers.
        </p>
        <p>
          Les échanges réalisés via WhatsApp sont soumis aux conditions d&rsquo;utilisation et à la politique de
          confidentialité de WhatsApp (Meta).
        </p>
      </Block>
      <Block title="Vos droits">
        <p>
          Conformément au Règlement général sur la protection des données (RGPD), vous pouvez exercer vos droits
          d&rsquo;accès, de rectification et de suppression des données nous concernant en nous contactant au{" "}
          <a href="tel:+594694449985" className="text-gold hover:underline">
            +594 694 44 99 85
          </a>
          .
        </p>
      </Block>
      <Block title="Mesure d'audience">
        <p>
          Le site utilise un outil de mesure d&rsquo;audience à des fins statistiques. Voir la page «&nbsp;Gestion des
          cookies&nbsp;».
        </p>
      </Block>
    </>
  );
}

function Cookies() {
  return (
    <>
      <Block title="Cookies utilisés">
        <p>Le site n&rsquo;utilise aucun cookie publicitaire et n&rsquo;effectue aucun suivi à des fins commerciales.</p>
        <p>
          Un outil de mesure d&rsquo;audience peut déposer des traceurs techniques afin d&rsquo;établir des
          statistiques anonymes de fréquentation (pages consultées, durée de visite). Ces données ne permettent pas
          de vous identifier personnellement.
        </p>
      </Block>
      <Block title="Paramétrer vos préférences">
        <p>
          Vous pouvez à tout moment configurer votre navigateur pour refuser ou supprimer les cookies (rubrique
          «&nbsp;Confidentialité&nbsp;» ou «&nbsp;Cookies&nbsp;» des réglages de votre navigateur). Le refus des
          cookies n&rsquo;altère en rien la consultation du site.
        </p>
      </Block>
    </>
  );
}

export function LegalPages({ page, onClose }) {
  useEffect(() => {
    if (!page) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [page, onClose]);

  if (!page) return null;

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-background" data-testid={`legal-page-${page}`}>
      <div className="mx-auto max-w-3xl px-6 py-20 lg:px-10">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          data-testid="legal-page-close"
          className="fixed right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background text-foreground transition hover:border-gold hover:text-gold"
        >
          <X className="h-5 w-5" />
        </button>
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">L&rsquo;Eskale Guyane</p>
        <h2 className="mt-4 font-display text-4xl italic sm:text-5xl">{LEGAL_TITLES[page]}</h2>
        <div className="hairline mt-6 w-16" />
        {page === "mentions" && <Mentions />}
        {page === "confidentialite" && <Confidentialite />}
        {page === "cookies" && <Cookies />}
        <button
          type="button"
          onClick={onClose}
          className="mt-14 inline-flex items-center border border-foreground px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition hover:bg-foreground hover:text-background"
        >
          Retour au site
        </button>
      </div>
    </div>
  );
}
